/**
 * Bottom Sheet Component
 *
 * A mobile-first panel that slides up from the bottom of the screen.
 * Supports drag-to-dismiss via a handle, backdrop click to close,
 * and Escape key to close.
 *
 * @element peach-bottom-sheet
 *
 * @fires close - Emitted when the sheet should be closed (user dismissed it)
 *
 * @remarks
 * Uses light DOM for Tailwind compatibility. Since slots don't work in
 * light DOM, content must be passed via the `content` property as a
 * TemplateResult, not as children.
 *
 * @example
 * ```typescript
 * html`
 *   <peach-bottom-sheet
 *     ?open=${this.open}
 *     .content=${html`<div>Sheet content here</div>`}
 *     @close=${this.handleClose}
 *   ></peach-bottom-sheet>
 * `
 * ```
 */

import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { cn } from "@/utils/cn";

/**
 * Height presets for the bottom sheet
 * - partial: ~60vh, leaves map/content visible above
 * - full: full viewport height
 */
export type BottomSheetHeight = "partial" | "full";

@customElement("peach-bottom-sheet")
export class BottomSheetComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Whether the sheet is open
   */
  @property({ type: Boolean, reflect: true })
  declare open: boolean;

  /**
   * Height preset for the sheet
   */
  @property({ type: String })
  declare height: BottomSheetHeight;

  /**
   * Accessible label for the sheet dialog
   */
  @property({ type: String, attribute: "aria-label" })
  declare label: string;

  /**
   * Content to render inside the sheet (light DOM can't use slots)
   */
  @property({ attribute: false })
  declare content: TemplateResult | null;

  /**
   * Tracks touch/pointer start position for drag-to-dismiss
   */
  @state()
  declare private dragStartY: number | null;

  /**
   * Current drag offset in pixels (positive = dragging down)
   */
  @state()
  declare private dragOffset: number;

  private handleEscapeKey = this.onEscapeKey.bind(this);

  constructor() {
    super();
    this.open = false;
    this.height = "partial";
    this.label = "Sheet";
    this.content = null;
    this.dragStartY = null;
    this.dragOffset = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.handleEscapeKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.handleEscapeKey);
  }

  private onEscapeKey(e: KeyboardEvent) {
    if (e.key === "Escape" && this.open) {
      this.emitClose();
    }
  }

  /**
   * Emit the close event - parent is responsible for setting open=false
   */
  private emitClose() {
    this.dragOffset = 0;
    this.dragStartY = null;
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      this.emitClose();
    }
  }

  private handleDragStart(e: TouchEvent | PointerEvent) {
    const clientY = e instanceof TouchEvent ? e.touches[0].clientY : e.clientY;
    this.dragStartY = clientY;
    this.dragOffset = 0;

    // Capture pointer events to the handle element so we get moves/ups
    // even when the finger slides off the handle
    if (e instanceof PointerEvent) {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
  }

  private handleDragMove(e: TouchEvent | PointerEvent) {
    if (this.dragStartY === null) return;

    const clientY = e instanceof TouchEvent ? e.touches[0].clientY : e.clientY;
    const delta = clientY - this.dragStartY;

    // Only allow dragging downward
    this.dragOffset = Math.max(0, delta);

    // Prevent scroll while dragging handle
    e.preventDefault();
  }

  private handleDragEnd() {
    if (this.dragStartY === null) return;

    // Dismiss if dragged more than 100px down
    const shouldDismiss = this.dragOffset > 100;

    if (shouldDismiss) {
      this.emitClose();
    } else {
      this.dragOffset = 0;
    }

    this.dragStartY = null;
  }

  /**
   * Render the drag handle bar
   */
  private renderHandle() {
    return html`
      <div
        class="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none select-none flex-shrink-0"
        @touchstart=${this.handleDragStart}
        @touchmove=${this.handleDragMove}
        @touchend=${this.handleDragEnd}
        @pointerdown=${this.handleDragStart}
        @pointermove=${this.handleDragMove}
        @pointerup=${this.handleDragEnd}
        @pointercancel=${this.handleDragEnd}
      >
        <div class="w-10 h-1.5 bg-gray-300 rounded-full"></div>
      </div>
    `;
  }

  render() {
    // Don't render anything when closed (light DOM doesn't hide slotted content)
    if (!this.open) return null;

    const sheetHeight = this.height === "full" ? "h-[90dvh]" : "h-[65dvh]";

    const translateStyle = this.dragOffset > 0
      ? `transform: translateY(${this.dragOffset}px);`
      : "";

    return html`
      <!-- Backdrop -->
      <div
        class=${cn(
          "fixed inset-0 z-40 bg-black/40",
        )}
        @click=${this.handleBackdropClick}
        aria-hidden="true"
      ></div>

      <!-- Sheet panel -->
      <div
        role="dialog"
        aria-modal="true"
        aria-label=${this.label}
        style=${translateStyle}
        class=${cn(
          "fixed bottom-0 left-0 right-0 z-50",
          "bg-cream rounded-t-2xl shadow-2xl",
          "flex flex-col overflow-hidden",
          sheetHeight,
        )}
      >
        ${this.renderHandle()}
        ${this.content}
      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-bottom-sheet": BottomSheetComponent;
  }
}
